import {mix, Eventable} from '../mixin';
import audioCore from '../service/audioCore'; // eslint-disable-line
import Gainable from '../mixin/Gainable';

const {setTimeout, clearTimeout, Math, Number} = globalThis;
const {POSITIVE_INFINITY} = Number;

/** Optionen für das Abspielen eines Audios
 * @typedef {Object} Audio~AudioOptions
 * @property {number=} volume ``0.0`` bis ``1.0``
 * @property {number=} delay in Sekunden
 * @property {number=} loops
 * @property {LipSyncer=} lipSyncer
 * @property {string=} channel Falls gerade ein anderes Audio im Channel abgespielt wird, wird dieses gestoppt.
 * @property {Object=} once Objekt mit Events, auf die einmal gehört werden soll (Format: ``'type': handler``)
 * @property {Object=} on Objekt mit Events, auf die gehört werden soll (Format: ``'type': handler``)
 * @property {number|Array=} queueGap Abstand der Audios in einer Queue in Sekunden. Wird ein Array übergeben, so steht
 * jeder Index für den Index der Audios in der Queue. Nur so kann man auch dem ersten Audio ein Delay geben.
 */

class Audio extends mix().with(Eventable, Gainable)
{
    #id = '';
    #buffer;
    #namespace = '';
    #offset;
    #duration;
    #delay = 0;
    #startedAt = null;
    #pausedAt = null;

    #paused = true;
    #unloaded = false;
    #ended = false;

    #context = null;
    #source = null;
    #actionQueue = null;
    #delayTimer = null;
    #lipSyncer = null;
    #channel = null;

    #loopsToPlay = 0;
    #loopsPlayed = 0;

    /**
     * @mixes Gainable
     * @mixes Eventable
     * @borrows Gainable#muted as #muted
     * @borrows Gainable#volume as #volume
     * @borrows Gainable#gainNode as #gainNode
     * @borrows Gainable#fadeTween as #fadeTween
     * @borrows Gainable#fade as #fade
     * @hideconstructor
     */
    constructor({
        id, buffer, destinationNode, namespace, offset, duration
    })
    {
        super();

        this.#id = id;
        this.#context = audioCore.context;
        this.#buffer = buffer;
        this.#namespace = namespace || '__default';
        this.#offset = offset || 0;
        this.#duration = duration || this.#getBuffer().duration;
        this.gainNode.connect(destinationNode);
    }

    get _id()
    {
        console.warn('Please use the ".id" getter.');
        return this.#id;
    }

    /** Getter
     * @type {string}
     */
    get id()
    {
        return this.#id;
    }

    /** Getter
     * @type {string}
     */
    get namespace()
    {
        return this.#namespace;
    }

    get isFading()
    {
        console.warn('Please use the ".fading" getter.');
        return this.fading;
    }

    /** Getter
     * @type {boolean}
     */
    get fading()
    {
        return !!this.fadeTween;
    }

    /** Getter
     * @type {boolean}
     */
    get playing()
    {
        return !this.#paused;
    }

    /** Getter: Länge in Sekunden.
     * @type {number}
     */
    get duration()
    {
        return this.#duration;
    }

    /** Getter/Setter: Aktuelle Position in Sekunden.
     * @type {number}
     */
    get seek()
    {
        if (this.#ended)
        {
            return this.#duration;
        }
        if (this.#startedAt === null)
        {
            return 0;
        }
        const time = this.#paused ? this.#pausedAt : this.#context.currentTime;
        const seek = time - (this.#startedAt + this.#delay);
        return (seek < 0) ? 0 : seek;
    }

    set seek(seek)
    {
        if (typeof seek !== 'number' || seek < 0 || seek > this.duration)
        {
            return;
        }

        // in active or paused delay the audio will always be flagged as paused
        if (this.#delayTimer)
        {
            this.#paused = false;
            this.#clearDelayTimer();
            // edge case event: seeked while in active delay phase
            this.fire('play');
        }

        this.#startedAt += this.#delay;
        this.#delay = 0;
        this.#pausedAt = this.#startedAt + seek;

        if (!this.#paused)
        {
            this.#play();
        }
    }

    /** Getter/Setter: Fortschritt zwischen 0 und 1.
     * @type {number}
     */
    get progress()
    {
        return this.seek / this.duration;
    }

    set progress(progress)
    {
        if (typeof progress !== 'number' || progress < 0 || progress > 1)
        {
            return;
        }
        this.seek = this.duration * progress;
    }

    get eventable()
    {
        console.warn('Deprecated. Audio is always eventable.');
        return true;
    }

    getDuration()
    {
        console.warn('Deprecated. Use the ".duration" getter.');
        return this.duration;
    }

    #getBuffer()
    {
        if (typeof this.#buffer === 'string')
        {
            return audioCore._getBuffer(this.#buffer);
        }
        return this.#buffer;
    }

    /** Spielt das Audio ab.
     * @param {Audio~AudioOptions} options
     * @return {Audio}
     */
    play(options = {})
    {
        if (
            this.#isUnloaded() ||
            (!audioCore.unlockQueueActive && !audioCore.unlocked && !audioCore._unlockStarted) ||
            !this.#paused
        )
        {
            return this;
        }

        if (typeof options.loops === 'number')
        {
            this.#loopsToPlay = options.loops === -1 ? POSITIVE_INFINITY : options.loops;
            this.#loopsPlayed = 0;
        }

        if (typeof options.oncePlayed === 'function')
        {
            console.warn('Please use the new "once" option instead of "oncePlayed".');
            this.once('play', options.oncePlayed);
        }

        if (options.once)
        {
            const {once} = options;
            for (const type in once)
            {
                this.once(type, once[type]);
            }
        }

        if (options.on)
        {
            const {on} = options;
            for (const type in on)
            {
                this.on(type, on[type]);
            }
        }

        if (options.lipSyncer)
        {
            this.#lipSyncer = options.lipSyncer;
        }

        if (this.#lipSyncer)
        {
            /** Wenn ein LipSyncer einem Audio übergeben wurde, wird dieses Event vor dem Abspielen
             * gefeuert, um die Phasen-Reihen der Lippen zu setzen.
             * @event LipSyncer#audioPlay
             * @property {string} id
             * @property {string} namespace
             */
            this.#lipSyncer.fire('audioPlay', {id: this.#id, namespace: this.#namespace});
        }

        if (typeof options.volume === 'number')
        {
            this.volume = options.volume;
        }

        if (typeof options.delay === 'number')
        {
            this.#delay = options.delay;
        }

        if (typeof options.channel === 'string')
        {
            this.#channel = options.channel;
        }

        if (options.eventable)
        {
            console.warn('The "eventable" option is not needed anymore. All Audios are eventable.');
        }

        if (this.#actionQueue)
        {
            this.#actionQueue = this.#actionQueue.then(() => this.#play());
        }
        else
        {
            this.#actionQueue = audioCore.unlockedPromise
                .then(() => audioCore.resumeContext())
                .then(() => this.#play());
        }

        this.#ended = false;

        return this;
    }

    #play()
    {
        if (this.#isUnloaded()) { return; }

        const buffer = this.#getBuffer();
        const {currentTime} = this.#context;

        let source = this.#source;
        let seekedWhilePlaying = false;
        let delay = this.#delay;
        let offset = this.#offset;
        let duration = this.#duration;

        // this case must be seek while playing
        if (source && !delay)
        {
            seekedWhilePlaying = true;
        }

        this.#stopSource();
        source = this.#context.createBufferSource();
        source.buffer = buffer;
        source.connect(this.gainNode);
        source.onended = () => this.#end();
        this.#source = source;

        if (this.#pausedAt !== null)
        {
            let playOffset = this.#pausedAt - this.#startedAt;
            this.#startedAt = currentTime - playOffset;

            if (delay)
            {
                const delayOffset = delay - playOffset;
                if (delayOffset < 0)
                {
                    delay = 0;
                    playOffset = -delayOffset;
                }
                else
                {
                    delay = delayOffset;
                    playOffset = 0;
                }
            }

            offset += playOffset;
            if (duration)
            {
                duration -= playOffset;
            }

            this.#pausedAt = null;
        }
        else
        {
            this.#startedAt = currentTime;
        }

        if (typeof source.start === 'undefined')
        {
            source.noteGrainOn(delay ? (currentTime + delay) : 0, offset, duration);
        }
        else
        {
            source.start(delay ? (currentTime + delay) : 0, offset, duration);
        }

        audioCore._playAudio(this, this.#namespace);
        if (this.#channel)
        {
            audioCore.setChannel(this.#channel, this);
        }
        this.#actionQueue = null;

        if (delay)
        {
            this.#clearDelayTimer();
            this.#delayTimer = setTimeout(() =>
            {
                this.#clearDelayTimer();
                this.#startPlayback();
            }, Math.round(delay * 1000));
        }
        else
        {
            this.#startPlayback(seekedWhilePlaying);
        }
    }

    #startPlayback(seekedWhilePlaying = false)
    {
        this.#paused = false;

        const lipSyncer = this.#lipSyncer;

        if (!seekedWhilePlaying)
        {
            /**
             * @event Audio#play
             */
            this.fire('play');

            if (lipSyncer)
            {
                lipSyncer.play();
            }
            return;
        }

        if (lipSyncer)
        {
            lipSyncer.progress(this.seek);
        }
    }

    /** Pausiert das Audio.
     * @return {Audio}
     */
    pause()
    {
        if (this.#actionQueue)
        {
            this.#actionQueue = this.#actionQueue.then(() => this.pause());
            return this;
        }

        if (this.#isUnloaded() || (this.#paused && !this.#delayTimer))
        {
            return this;
        }

        const {currentTime} = this.#context;

        if (this.#delayTimer)
        {
            this.#pausedAt = currentTime;
            this.#clearDelayTimer();
        }
        else if (!this.#paused)
        {
            this.#pausedAt = currentTime;
            this.#paused = true;

            /**
             * @event Audio#pause
             */
            this.fire('pause');

            if (this.#lipSyncer !== null)
            {
                this.#lipSyncer.pause();
            }
        }

        this.#stopSource();
        audioCore._pauseAudio(this, this.#namespace);

        return this;
    }

    /** Stoppt das Audio.
     * @return {Audio}
     */
    stop()
    {
        if (this.#actionQueue)
        {
            this.#actionQueue = this.#actionQueue.then(() => this.stop());
            return this;
        }

        if (this.#isUnloaded())
        {
            return this;
        }

        if (this.#startedAt !== null)
        {
            this.#stopOrEnd();

            /**
             * @event Audio#stop
             */
            this.fire('stop');
        }

        this.#loopsToPlay = 0;
        this.#loopsPlayed = 0;

        return this;
    }

    #end()
    {
        this.#stopOrEnd();
        this.#ended = true;
        /**
         * @event Audio#end
         */
        this.fire('end');
        if (this.#loopsToPlay)
        {
            this.#loopsPlayed++;
            if (this.#loopsPlayed <= this.#loopsToPlay)
            {
                /**
                 * @event Audio#loop
                 */
                this.fire('loop');
                this.play();
            }
            else
            {
                this.#loopsToPlay = 0;
                this.#loopsPlayed = 0;
                /**
                 * @event Audio#loopsEnd
                 */
                this.fire('loopsEnd');
            }
        }
    }

    #stopOrEnd()
    {
        this.#stopSource();
        this.#clearDelayTimer();
        this.#pausedAt = null;
        this.#startedAt = null;
        this.#paused = true;
        this.#delay = 0;
        audioCore._stopAudio(this, this.#namespace);
        if (this.#lipSyncer !== null)
        {
            this.#lipSyncer.stop();
        }
    }

    #stopSource()
    {
        const source = this.#source;
        if (source)
        {
            if (typeof source.stop === 'undefined')
            {
                source.noteOff();
            }
            else
            {
                source.stop();
            }

            source.onended = null;

            setTimeout(() =>
            {
                source.disconnect();
                try
                {
                    source.buffer = null;
                }
                catch (e)
                {
                    source.buffer = audioCore.scratchBuffer;
                }
            }, 250);

            this.#source = null;
        }
    }

    #clearDelayTimer()
    {
        if (this.#delayTimer)
        {
            clearTimeout(this.#delayTimer);
            this.#delayTimer = null;
        }
    }

    #isUnloaded()
    {
        if (this.#unloaded)
        {
            return true;
        }
        if (!this.#getBuffer())
        {
            this.unload();
            return true;
        }
        return false;
    }

    fade(to, duration, oldDuration)
    {
        if (this.#isUnloaded())
        {
            return this;
        }

        if (oldDuration)
        {
            console.warn('The new method signature of "::fade" is "(to, duration)"!');
            to = duration;
            duration = oldDuration;
        }

        super.fade(to, duration);
        this.fadeTween.once('complete', () => this.fire('fadeEnd'));

        return this;
    }

    /**
     * Stoppt das Audio und ermöglicht schnellere Speicherfreigabe.
     */
    unload()
    {
        if (this.#unloaded) { return; }

        this.#actionQueue = null;
        this.#stopOrEnd();
        this.#buffer = null;
        this.#lipSyncer = null;
        this._destroyFadeTween();
        this._destroyGainNode();
        this.#context = null;
        this.#unloaded = true;
    }
}

export default Audio;
